// Auth hook — wraps Supabase session into React state so App can render
// LoginScreen / Loading / authenticated content based on the current user.
//
// Dev bypass: ?dev=1 in the URL stamps a fake local user so the app can
// be tested end-to-end without Google sign-in. Gated to import.meta.env.DEV
// (true under `vite dev` — local + the e2e harness — and compiled out of
// production builds), so it cannot bypass auth on deployed builds (#59).
//
// Boot-failure handling (P-SILENT-FAILURE-BATCH, QA-AUDIT Area 1 P1): the
// initial getSession() is funnelled through resolveInitialAuthUser (pure,
// never-throws). If it fails we STILL clear `loading` so the app falls
// through to the login screen instead of hanging on <Loading/> forever, and
// we surface the failure (Sentry + a toast) so the parent has a visible
// state and an action. useAuth runs under <ToastProvider> (main.jsx tree:
// ToastProvider > RouterProvider > __root > App), so useToast() is safe.

import { useEffect, useState } from "react";
import { supabase, ensureParentRow } from "../supabaseClient.js";
import { resolveInitialAuthUser } from "./authResolve.js";
import { captureSupabaseError } from "../lib/sentry";
import { useToast } from "../context/ToastContext.jsx";
// MA-1056: a sign-up that came through the website's /teacher/start entry
// records its source tag on the new account (fire-and-forget, never awaited).
import { noteTeacherStartSignIn } from "../lib/teacher/pendingTeacherStart.ts";

export default function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // showToast is useCallback([])-stable, so listing it in the effect deps
  // below does not re-run the effect.
  const { showToast } = useToast();

  useEffect(() => {
    if (
      import.meta.env.DEV &&
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("dev") === "1"
    ) {
      setUser({ id: "dev-local", email: "dev@local" });
      setLoading(false);
      return;
    }

    // Guards a late resolve from writing into an unmounted component (and
    // ignores the resolve if the subscription has already torn down).
    let alive = true;

    resolveInitialAuthUser(supabase).then(({ user: resolvedUser, error }) => {
      if (!alive) return;
      if (error) {
        // getSession rejected/errored at boot. DO NOT swallow: report it and
        // tell the parent. user stays null → App renders the login screen,
        // giving the parent an action instead of an indefinite spinner.
        captureSupabaseError("getSession rejected at boot", error);
        showToast(
          "We couldn't check your sign-in. Please try signing in again.",
          "error",
          6000,
        );
      }
      setUser(error ? null : resolvedUser);
      setLoading(false);
      // MA-02: defense-in-depth behind the on_auth_user_created trigger.
      // Fire-and-forget — never blocks the app render path.
      if (!error && resolvedUser) ensureParentRow(resolvedUser);
      if (!error && resolvedUser) void noteTeacherStartSignIn(resolvedUser, supabase);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      if (!alive) return;
      setUser(s?.user ?? null);
      // MA-02: also guarantee the parents row on an explicit sign-in event
      // (covers the login-from-screen path not reached by resolveInitialAuthUser).
      if (event === 'SIGNED_IN' && s?.user) ensureParentRow(s.user);
      if (event === 'SIGNED_IN' && s?.user) void noteTeacherStartSignIn(s.user, supabase);
    });
    return () => {
      alive = false;
      subscription.unsubscribe();
    };
  }, [showToast]);

  return { user, loading };
}
